import { useEffect, useState } from 'react';
import { ShouldShowAlertProps, StorageShape, StoredItem } from './interfaces';

const STORAGE_KEY = 'ShouldShowAlert';
const DAY_IN_MS = 24 * 60 * 60 * 1000;

export function useShouldShowAlert({ config, alertKey }: ShouldShowAlertProps) {
	const [showModal, setShowModal] = useState(false);
	const [showAlert, setShowAlert] = useState(false);

	const { modalViews = 0, alertDays = 0, id } = config;

	const getStorage = (): StorageShape => {
		try {
			const json = localStorage.getItem(STORAGE_KEY);
			return json ? JSON.parse(json) : {};
		} catch {
			return {};
		}
	};

	const saveStorage = (storage: StorageShape) => localStorage.setItem(STORAGE_KEY, JSON.stringify(storage));

	const updateStorage = (item: { type: 'modal'; views: number } | { type: 'alert'; expiresAt: number }) => {
		const storage = getStorage();
		const updated: StorageShape = {
			...storage,
			[alertKey]: {
				...(storage[alertKey] || {}),
				[id]: item,
			},
		};
		saveStorage(updated);
		return updated;
	};

	const handleModal = (item: { type: 'modal'; views: number }) => {
		const sessionKey = `${id}-session`;

		if (!sessionStorage.getItem(sessionKey)) {
			setShowModal(true);
			sessionStorage.setItem(sessionKey, 'true');

			updateStorage({
				type: 'modal',
				views: item.views + 1,
			});
		}
	};

	const handleAlert = (item?: { type: 'alert'; expiresAt?: number }) => {
		if (!alertDays) return;

		const expiresAt = item?.expiresAt ?? Date.now() + alertDays * DAY_IN_MS;

		if (!item?.expiresAt) {
			updateStorage({ type: 'alert', expiresAt });
		}

		if (Date.now() <= expiresAt) {
			setShowAlert(true);
		}
	};

	const getInitialItem = (): StoredItem | null => {
		if (modalViews === 0 && alertDays > 0) {
			const expiresAt = Date.now() + alertDays * DAY_IN_MS;
			updateStorage({ type: 'alert', expiresAt });
			setShowAlert(true);
			return null;
		}

		return { type: 'modal', views: 0 } as const;
	};

	const processModal = (item: { type: 'modal'; views: number }) => {
		if (item.views < modalViews) {
			handleModal(item);
			return;
		}
	};

	useEffect(() => {
		const storage = getStorage();
		let item: StoredItem | null = storage[alertKey]?.[id];

		if (!item) {
			item = getInitialItem();
			if (!item) return;
		}

		if (item.type === 'modal') {
			const sessionKey = `${id}-session`;

			if (item.views < modalViews) {
				processModal(item);
				return;
			}

			if (!sessionStorage.getItem(sessionKey) && alertDays > 0) {
				updateStorage({
					type: 'alert',
					expiresAt: Date.now() + alertDays * DAY_IN_MS,
				});
				setShowAlert(true);
			}

			return;
		}

		if (item.type === 'alert') {
			handleAlert(item);
		}
	}, [id, modalViews, alertDays, alertKey]);

	return { showModal, showAlert };
}
